import type { Task } from "@app/core";
import { effectivePriorityOf, PRIORITY_RANK, startOfDay } from "@app/core";
import { Card } from "@app/ui";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { AiQuickAdd } from "../features/ai-input/AiQuickAdd.js";
import { DaySection } from "../features/agenda/DaySection.js";
import { NextUpCard } from "../features/agenda/NextUpCard.js";
import { OVERDUE_COLLAPSED, OVERDUE_PAGE, OverdueSection, type OverdueSort } from "../features/agenda/OverdueSection.js";
import { SnoozedSection } from "../features/agenda/SnoozedSection.js";
import { TodayGrid } from "../features/agenda/TodayGrid.js";
import { TodayHero } from "../features/agenda/TodayHero.js";
import { WeekStrip } from "../features/agenda/WeekStrip.js";
import { buildAgenda, dayPreview, dayProgress, dueWhenMovedToToday } from "../features/agenda/agenda.js";
import type { TodayModuleId } from "../features/agenda/layout.js";
import { dayAgendaNavKey, useAgendaKeyboardNav } from "../features/agenda/useAgendaKeyboardNav.js";
import { cutUpcoming, rowsThroughDay } from "../features/agenda/upcoming-cut.js";
import { useTodayPlan } from "../features/agenda/useTodayPlan.js";
import type { RuleExpansionCache } from "../features/calendar-view/build-days.js";
import { useNowMinute } from "../features/calendar-view/useNowMinute.js";
import { tasksForInbox } from "../features/inbox/inbox-list.js";
import { PlanDayDialog } from "../features/matrix-view/PlanDayDialog.js";
import { TimeLadder, type LadderChoice } from "../features/matrix-view/TimeLadder.js";
import { ladderDurationMinutes, sameLadderSchedule } from "../features/matrix-view/ladder-schedule.js";
import { useEffectivePriorityCtx } from "../features/priority-matrix/useEffectiveMatrixDisplay.js";
import { SnoozeMenu } from "../features/snooze/index.js";
import { toLocalDate } from "../features/task-form/time-helpers.js";
import { visibleTasks } from "../lib/calendar-visibility.js";
import { isDemoBoot } from "../lib/demo-mode.js";
import { useReducedMotion } from "../lib/motion/index.js";
import { LIST_PAGE, ShowMoreFooter } from "../lib/paged-list.js";
import { useI18n } from "../lib/useI18n.js";
import { useViewFilters } from "../lib/useViewFilters.js";
import { applyViewExclusions } from "../lib/view-exclusions.js";
import { occurrenceRefOf } from "../lib/virtual-id.js";
import { useCalendarFocusStore, useCalendarsStore, useSettingsStore, useTaskDialogStore, useTasksStore } from "../stores/index.js";
import { toast } from "../stores/toast.store.js";
import { beginUndoable, endUndoable, undoAction } from "../stores/undo.js";

/** Device-local view state (never synced). */
const HIDE_DONE_KEY = "today.hideDone";
const OVERDUE_SORT_KEY = "today.overdueSort";
const COLLAPSE_KEY = "today.collapsed";

interface CollapseState {
  overdue: boolean;
  snoozed: boolean;
}

export function TodayRoute() {
  const { t } = useI18n();
  const tasks = useTasksStore((s) => s.tasks);
  const rules = useTasksStore((s) => s.rules);
  const overrides = useTasksStore((s) => s.overrides);
  const loaded = useTasksStore((s) => s.loaded);
  const hydrate = useTasksStore((s) => s.hydrate);
  const complete = useTasksStore((s) => s.complete);
  const completeOccurrence = useTasksStore((s) => s.completeOccurrence);
  const edit = useTasksStore((s) => s.edit);
  const detachWithPatch = useTasksStore((s) => s.detachWithPatch);
  const snooze = useTasksStore((s) => s.snooze);
  const unsnooze = useTasksStore((s) => s.unsnooze);
  const snoozeOccurrence = useTasksStore((s) => s.snoozeOccurrence);
  const calendars = useCalendarsStore((s) => s.calendars);
  const applyFilters = useViewFilters();
  const openCreate = useTaskDialogStore((s) => s.openCreate);
  const openEdit = useTaskDialogStore((s) => s.openEdit);
  const focusCalendar = useCalendarFocusStore((s) => s.focus);
  const lastCalendarView = useSettingsStore((s) => s.lastCalendarView);
  const excludedTopicIds = useSettingsStore((s) => s.todayExcludedTopicIds);
  const excludedCalendarIds = useSettingsStore((s) => s.todayExcludedCalendarIds);
  const agendaDays = useSettingsStore((s) => s.agendaDays);
  const showEmptyDays = useSettingsStore((s) => s.agendaShowEmptyDays);
  const morningHour = useSettingsStore((s) => s.snoozeMorningHour);
  const defaultDurationMin = useSettingsStore((s) => s.defaultDurationMin);
  const priorityCtx = useEffectivePriorityCtx();
  const navigate = useNavigate();
  const now = useNowMinute();
  const reduce = useReducedMotion();

  useEffect(() => { if (!loaded) void hydrate(); }, [loaded, hydrate]);

  const viewFilteredTasks = useMemo(() => applyFilters(tasks), [tasks, applyFilters]);
  const filteredTasks = useMemo(
    () => applyViewExclusions(viewFilteredTasks, { topicIds: excludedTopicIds, calendarIds: excludedCalendarIds }),
    [viewFilteredTasks, excludedTopicIds, excludedCalendarIds],
  );
  // Planner availability keeps calendar visibility + route exclusions but NOT
  // the interactive filters — a busy slot doesn't free up because it's filtered.
  const availabilityTasks = useMemo(
    () => applyViewExclusions(visibleTasks(tasks, calendars), { topicIds: excludedTopicIds, calendarIds: excludedCalendarIds }),
    [tasks, calendars, excludedTopicIds, excludedCalendarIds],
  );

  const cache = useRef<RuleExpansionCache>(new Map());
  const agenda = useMemo(
    () => buildAgenda(filteredTasks, rules, overrides, now, cache.current, agendaDays),
    [filteredTasks, rules, overrides, now, agendaDays],
  );

  const [snoozeTarget, setSnoozeTarget] = useState<{ task: Task; x: number; y: number } | null>(null);
  /** TimeLadder target: `day` is the local day the pick lands on; `notify`
   *  adds the "Moved to today" toast (the pull-onto-today actions). */
  const [ladder, setLadder] = useState<{ task: Task; x: number; y: number; day: Date; notify: boolean } | null>(null);
  const [overdueLimit, setOverdueLimit] = useState(OVERDUE_COLLAPSED);
  /** How many upcoming rows the agenda renders before its "show more". Lives
   *  here, like `overdueLimit`, because keyboard triage walks the rendered
   *  rows. See {@link cutUpcoming}. */
  const [upcomingLimit, setUpcomingLimit] = useState(LIST_PAGE);
  /** A strip jump waiting for `upcomingLimit` to grow far enough to render it. */
  const [pendingDayJump, setPendingDayJump] = useState<number | null>(null);
  const [hideDone, setHideDone] = useState(() => {
    if (isDemoBoot()) return false;
    try { return localStorage.getItem(HIDE_DONE_KEY) === "1"; } catch { return false; }
  });
  const [overdueSort, setOverdueSort] = useState<OverdueSort>(() => {
    if (isDemoBoot()) return "age";
    try { return localStorage.getItem(OVERDUE_SORT_KEY) === "priority" ? "priority" : "age"; } catch { return "age"; }
  });
  const [collapsed, setCollapsed] = useState<CollapseState>(() => {
    if (isDemoBoot()) return { overdue: false, snoozed: false };
    try {
      const v = JSON.parse(localStorage.getItem(COLLAPSE_KEY) ?? "{}") as Partial<CollapseState>;
      return { overdue: v.overdue === true, snoozed: v.snoozed === true };
    } catch {
      return { overdue: false, snoozed: false };
    }
  });
  const [bulkBusy, setBulkBusy] = useState<"move" | "snooze" | null>(null);
  // State disables the controls after render; the ref also closes the
  // same-tick window in which a double activation could start two sweeps.
  const bulkBusyRef = useRef(false);

  const today = agenda.days[0]!;
  const progress = dayProgress(today.items);
  const eventCount = today.items.filter((task) => task.is_event).length;
  const pick = agenda.nextUp;
  const scheduledMin = agenda.scheduledMinutes;
  const tomorrow = agenda.days[1] ? dayPreview(agenda.days[1].items) : null;
  const inboxCount = useMemo(
    // This chip opens the Inbox route, so Today-only topic/calendar exclusions
    // must not make its count disagree with the destination. Shared sidebar
    // filters and hidden-calendar visibility still apply through applyFilters.
    () => tasksForInbox(viewFilteredTasks).filter((x) => x.status === "open").length,
    [viewFilteredTasks],
  );

  const sortedOverdue = useMemo(() => {
    if (overdueSort === "age") return agenda.overdue;
    return [...agenda.overdue].sort(
      (a, b) => PRIORITY_RANK[effectivePriorityOf(b, priorityCtx)] - PRIORITY_RANK[effectivePriorityOf(a, priorityCtx)],
    );
  }, [agenda.overdue, overdueSort, priorityCtx]);

  const plan = useTodayPlan({ availabilityTasks, filteredTasks, rules, overrides, now });
  const actionBusy = bulkBusy !== null || plan.applying;

  function toggleHideDone() {
    setHideDone((v) => {
      const next = !v;
      if (!isDemoBoot()) {
        try { localStorage.setItem(HIDE_DONE_KEY, next ? "1" : "0"); } catch { /* private mode */ }
      }
      return next;
    });
  }

  function toggleOverdueSort() {
    setOverdueSort((v) => {
      const next = v === "age" ? "priority" : "age";
      if (!isDemoBoot()) {
        try { localStorage.setItem(OVERDUE_SORT_KEY, next); } catch { /* private mode */ }
      }
      return next;
    });
  }

  function toggleCollapsed(key: keyof CollapseState) {
    setCollapsed((v) => {
      const next = { ...v, [key]: !v[key] };
      if (!isDemoBoot()) {
        try { localStorage.setItem(COLLAPSE_KEY, JSON.stringify(next)); } catch { /* private mode */ }
      }
      return next;
    });
  }

  function toggleComplete(task: Task, done: boolean) {
    if (task.virtual && task.parent_id) void completeOccurrence(task.parent_id, occurrenceRefOf(task), done);
    else void complete(task.id, done);
  }

  function applySnooze(task: Task, atIso: string) {
    if (task.virtual && task.parent_id) void snoozeOccurrence(task.parent_id, occurrenceRefOf(task), atIso);
    else void snooze(task.id, atIso);
  }

  function wake(task: Task) {
    if (task.virtual && task.parent_id) void completeOccurrence(task.parent_id, occurrenceRefOf(task), false);
    else void unsnooze(task.id);
  }

  function viewOnCalendar(task: Task) {
    if (!task.due_at) return;
    focusCalendar(new Date(task.due_at), task.id);
    navigate(`/calendar/${lastCalendarView}`);
  }

  function openDayOnCalendar(date: Date) {
    focusCalendar(date);
    navigate("/calendar/day");
  }

  // The TimeLadder picks the slot; committing reschedules a real task in place
  // and DETACHES a missed occurrence (the series rhythm is untouched — same
  // one-off contract as an occurrence edit).
  async function commitLadder(choice: LadderChoice) {
    if (!ladder) return;
    const task = ladder.task;
    const due = new Date(ladder.day);
    due.setHours(0, 0, 0, 0);
    const allDay = choice.kind === "allDay";
    if (choice.kind === "time") due.setHours(choice.hour, choice.minute, 0, 0);
    const dueIso = due.toISOString();
    // Presets and the exact-time picker can still reproduce the task's
    // current slot even when the explicit current-state rung is hidden.
    // Treat that as a real no-op: no updated_at churn, snapshot, or toast.
    if (sameLadderSchedule(task, due, allDay)) return;
    const patch = {
      due_at: dueIso,
      all_day: allDay,
      duration_minutes: ladderDurationMinutes(task, allDay, defaultDurationMin),
    };
    let enteredUndo = false;
    let outer = false;
    try {
      enteredUndo = true;
      outer = await beginUndoable(ladder.notify ? "Move to today" : "Reschedule task");
      if (task.virtual && task.parent_id) {
        await detachWithPatch(task.parent_id, occurrenceRefOf(task), patch);
      } else {
        await edit(task.id, patch);
      }
      if (outer) {
        toast.withAction(
          "success",
          ladder.notify ? t("today.movedToToday") : t("today.rescheduled"),
          undoAction(),
        );
      }
    } catch {
      const message = ladder.notify ? t("today.moveFailed") : t("today.rescheduleFailed");
      if (outer) toast.withAction("error", message, undoAction());
      else toast.error(message);
    } finally {
      if (enteredUndo) endUndoable();
    }
  }

  const openMoveToToday = (task: Task, pos: { x: number; y: number }) =>
    setLadder({ task, ...pos, day: startOfDay(now), notify: true });
  const openReschedule = (day: Date) => (task: Task, pos: { x: number; y: number }) =>
    setLadder({ task, ...pos, day, notify: false });

  // Bulk triage — ONE undoable scope for the whole sweep, so a single Undo
  // reverts it (nested store calls neither snapshot nor toast).
  async function bulkMoveToToday() {
    if (bulkBusyRef.current) return;
    const items = [...agenda.overdue];
    if (items.length === 0) return;
    bulkBusyRef.current = true;
    setBulkBusy("move");
    let enteredUndo = false;
    let outer = false;
    try {
      enteredUndo = true;
      outer = await beginUndoable("Move overdue to today");
      for (const task of items) {
        const due = dueWhenMovedToToday(task, now);
        const patch = { due_at: due.toISOString(), all_day: task.all_day };
        if (task.virtual && task.parent_id) await detachWithPatch(task.parent_id, occurrenceRefOf(task), patch);
        else await edit(task.id, patch);
      }
      if (outer) toast.withAction("success", t("today.bulkMoved", { count: items.length }), undoAction());
    } catch {
      if (outer) toast.withAction("error", t("today.bulkMoveFailed"), undoAction());
      else toast.error(t("today.bulkMoveFailed"));
    } finally {
      if (enteredUndo) endUndoable();
      bulkBusyRef.current = false;
      setBulkBusy(null);
    }
  }

  async function bulkSnoozeTomorrow() {
    if (bulkBusyRef.current) return;
    const items = [...agenda.overdue];
    if (items.length === 0) return;
    bulkBusyRef.current = true;
    setBulkBusy("snooze");
    const at = new Date(now);
    at.setDate(at.getDate() + 1);
    at.setHours(morningHour, 0, 0, 0);
    const atIso = at.toISOString();
    let enteredUndo = false;
    let outer = false;
    try {
      enteredUndo = true;
      outer = await beginUndoable("Snooze overdue");
      for (const task of items) {
        if (task.virtual && task.parent_id) await snoozeOccurrence(task.parent_id, occurrenceRefOf(task), atIso);
        else await snooze(task.id, atIso);
      }
      if (outer) toast.withAction("success", t("today.bulkSnoozed", { count: items.length }), undoAction());
    } catch {
      if (outer) toast.withAction("error", t("today.snoozeFailed"), undoAction());
      else toast.error(t("today.snoozeFailed"));
    } finally {
      if (enteredUndo) endUndoable();
      bulkBusyRef.current = false;
      setBulkBusy(null);
    }
  }

  function jumpToSection(name: "overdue" | "snoozed") {
    document.querySelector(`[data-today-section="${name}"]`)
      ?.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "start" });
  }

  function scrollToDay(i: number) {
    document.querySelector(`[data-agenda-day="${i}"]`)
      ?.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "start" });
  }

  function jumpToDay(i: number) {
    if (document.querySelector(`[data-agenda-day="${i}"]`)) {
      scrollToDay(i);
      return;
    }
    // The day is past the render cap. Grow it to exactly cover that day and
    // scroll once the section exists — the strip must never point at nothing.
    setUpcomingLimit(rowsThroughDay(upcomingAll, i, hideDone, LIST_PAGE));
    setPendingDayJump(i);
  }

  const upcomingAll = useMemo(
    () => agenda.days
      .map((day, i) => ({ ...day, index: i }))
      .slice(1)
      .filter((day) => showEmptyDays || day.items.length > 0),
    [agenda.days, showEmptyDays],
  );
  const { upcoming, shownRows: upcomingShownRows, totalRows: upcomingTotalRows } = useMemo(
    () => cutUpcoming(upcomingAll, upcomingLimit, hideDone),
    [upcomingAll, upcomingLimit, hideDone],
  );

  useEffect(() => {
    if (pendingDayJump === null) return;
    scrollToDay(pendingDayJump);
    setPendingDayJump(null);
    // `upcoming` is the dependency that matters: the jump has to wait for the
    // grown cap to actually render its target section.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingDayJump, upcoming]);

  // Keyboard triage order = overdue (as displayed) then the agenda days —
  // exactly the RENDERED rows, which is why the cap lives here and not in
  // `DaySection`: navigating to a row that was never painted goes nowhere.
  const overdueNavRows = useMemo(
    () => actionBusy || collapsed.overdue ? [] : sortedOverdue.slice(0, overdueLimit),
    [actionBusy, collapsed.overdue, overdueLimit, sortedOverdue],
  );
  const navRows = useMemo(
    () => actionBusy ? [] : [
      ...overdueNavRows.map((task) => ({ key: `o:${task.id}`, task })),
      ...[{ ...today, limit: undefined }, ...upcoming].flatMap(({ date, items, limit }) => {
        const visible = hideDone ? items.filter((x) => x.status !== "done") : items;
        return (limit === undefined ? visible : visible.slice(0, limit))
          .map((task) => ({ key: dayAgendaNavKey(date, task.id), task }));
      }),
    ],
    [actionBusy, overdueNavRows, today, upcoming, hideDone],
  );
  const focusedKey = useAgendaKeyboardNav(navRows, {
    toggle: toggleComplete,
    edit: openEdit,
    snooze: (task, pos) => setSnoozeTarget({ task, ...pos }),
  });

  const modules: Partial<Record<TodayModuleId, React.ReactNode>> = {
    hero: (
      <TodayHero
        now={now}
        progress={progress}
        eventCount={eventCount}
        overdueCount={agenda.overdue.length}
        snoozedCount={agenda.snoozed.length}
        scheduledMin={scheduledMin}
        tomorrow={tomorrow}
        inboxCount={inboxCount}
        hideDone={hideDone}
        onToggleHideDone={toggleHideDone}
        onNew={() => openCreate()}
        onPlanDay={plan.openPlan}
        onOpenInbox={() => navigate("/inbox")}
        onJumpOverdue={() => jumpToSection("overdue")}
        onJumpSnoozed={() => jumpToSection("snoozed")}
      />
    ),
    week: <WeekStrip days={agenda.days} now={now} onJump={jumpToDay} emptyDaysVisible={showEmptyDays} />,
    quickAdd: <AiQuickAdd />,
    nextUp: pick ? <NextUpCard pick={pick} now={now} onEdit={openEdit} onView={viewOnCalendar} /> : null,
    overdue: sortedOverdue.length > 0 ? (
      <OverdueSection
        items={sortedOverdue}
        now={now}
        sort={overdueSort}
        onToggleSort={toggleOverdueSort}
        collapsed={collapsed.overdue}
        onToggleCollapsed={() => toggleCollapsed("overdue")}
        limit={overdueLimit}
        onShowMore={() => setOverdueLimit((n) => n + OVERDUE_PAGE)}
        onShowLess={() => setOverdueLimit(OVERDUE_COLLAPSED)}
        focusedKey={focusedKey}
        onToggle={toggleComplete}
        onEdit={openEdit}
        onView={viewOnCalendar}
        onSnooze={(task, pos) => setSnoozeTarget({ task, ...pos })}
        onMoveToToday={openMoveToToday}
        onBulkMoveToday={() => void bulkMoveToToday()}
        onBulkSnooze={() => void bulkSnoozeTomorrow()}
        bulkBusy={bulkBusy !== null}
      />
    ) : null,
    snoozed: agenda.snoozed.length > 0 ? (
      <SnoozedSection
        items={agenda.snoozed}
        collapsed={collapsed.snoozed}
        onToggleCollapsed={() => toggleCollapsed("snoozed")}
        onEdit={openEdit}
        onWake={wake}
      />
    ) : null,
    today: (
      <div data-agenda-day={0} className="scroll-mt-4">
        <DaySection
          date={today.date}
          items={today.items}
          now={now}
          isToday
          hideDone={hideDone}
          focusedKey={focusedKey}
          onToggle={toggleComplete}
          onEdit={openEdit}
          onView={viewOnCalendar}
          onSnooze={(task, pos) => setSnoozeTarget({ task, ...pos })}
          onReschedule={openReschedule(today.date)}
          onOpenDay={openDayOnCalendar}
        />
      </div>
    ),
    upcoming: upcoming.length > 0 ? (
      <div className="flex flex-col gap-5">
        {upcoming.map(({ date, items, index, limit }) => (
          <div key={date.toISOString()} data-agenda-day={index} className="scroll-mt-4">
            <DaySection
              date={date}
              items={items}
              now={now}
              isToday={false}
              hideDone={hideDone}
              focusedKey={focusedKey}
              onToggle={toggleComplete}
              onEdit={openEdit}
              onView={viewOnCalendar}
              onSnooze={(task, pos) => setSnoozeTarget({ task, ...pos })}
              onMoveToToday={openMoveToToday}
              onReschedule={openReschedule(date)}
              onOpenDay={openDayOnCalendar}
              limit={limit}
            />
          </div>
        ))}
        {upcomingShownRows < upcomingTotalRows && (
          <Card className="overflow-hidden p-0">
            <ShowMoreFooter
              shown={upcomingShownRows}
              total={upcomingTotalRows}
              onShowMore={() => setUpcomingLimit((n) => n + LIST_PAGE)}
              className="border-t-0"
            />
          </Card>
        )}
      </div>
    ) : null,
  };

  return (
    <div className="w-full pb-8">
      <div aria-busy={actionBusy || undefined} inert={actionBusy || undefined} className={actionBusy ? "cursor-wait" : undefined}>
        <TodayGrid modules={modules} />
      </div>

      {snoozeTarget && (
        <SnoozeMenu
          x={snoozeTarget.x}
          y={snoozeTarget.y}
          onPick={(atIso) => applySnooze(snoozeTarget.task, atIso)}
          onClose={() => setSnoozeTarget(null)}
        />
      )}

      {ladder && (
        <TimeLadder
          x={ladder.x}
          y={ladder.y}
          dayKey={toLocalDate(ladder.day)}
          task={ladder.task}
          showCurrentChoice={
            !!ladder.task.due_at
            && toLocalDate(new Date(ladder.task.due_at)) !== toLocalDate(ladder.day)
          }
          onPick={(choice) => void commitLadder(choice)}
          onClose={() => setLadder(null)}
        />
      )}

      {plan.planOpen && plan.plan && (
        <PlanDayDialog
          day={startOfDay(now)}
          plan={plan.plan}
          onApply={(slots) => void plan.applyPlan(slots)}
          onClose={plan.closePlan}
        />
      )}
    </div>
  );
}

